<template>
  <label
    ref="target"
    class="input-select group relative block w-full text-md text-slate"
    :class="[
      isMenuOpen ? 'z-50' : 'z-[1]',
      disabled && 'pointer-events-none opacity-50',
    ]"
    @click.prevent="toggleMenu()"
  >
    <slot name="icon">
      <UiIcon
        v-if="icon"
        :icon="icon"
        class="input-select__icon z-[2] shrink-0"
      />
    </slot>
    <span
      v-if="label"
      class="absolute -top-1.5 left-3.5 z-10 whitespace-nowrap bg-white px-1"
    >
      {{ label }}
    </span>

    <input
      v-if="!inputFitWidth"
      v-model="valueLocal"
      readonly
      autocomplete="off"
      class="input-select__input relative z-[2] h-16 w-full cursor-pointer border border-gray-lighten1 px-6 text-md transition placeholder:text-slate-lighten4 focus:outline-0 focus:outline-offset-0 focus:ring-0 group-hover:border-gray"
      :class="[
        { '!border-red-lighten1': error },
        isMenuOpen ? 'rounded-t-lg' : 'rounded-lg',
      ]"
      :placeholder="placeholder"
    />
    <span
      v-else
      class="input-select__input relative z-[2] h-16 w-full cursor-pointer border border-gray-lighten1 px-6 text-md transition placeholder:text-slate-lighten4 focus:outline-0 focus:outline-offset-0 focus:ring-0 group-hover:border-gray"
      :class="[
        { '!border-red-lighten1': error },
        isMenuOpen ? 'rounded-t-lg' : 'rounded-lg',
      ]"
    >
      {{ valueLocal }}
    </span>

    <button
      type="button"
      aria-label="Open menu"
      class="input-select__toggle absolute right-9 top-1/2 z-[2] -translate-y-1/2"
      :class="{ 'z-[2]': isMenuOpen }"
    >
      <UiIcon
        :icon="isMenuOpen ? 'chevronUp' : 'chevronDown'"
        class="h-4 w-4"
      />
    </button>

    <transition
      enter-active-class="transform duration-300"
      enter-from-class="opacity-0 translate-y-4"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transform duration-300"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-4"
    >
      <div
        v-if="isMenuOpen"
        class="input-select__menu absolute left-0 right-0 top-0 z-[1] overflow-hidden rounded-lg border-[1px] border-t-0 border-gray bg-white pt-20 shadow-default"
      >
        <div
          class="max-h-[312px] overflow-y-auto bg-white scrollbar scrollbar-track-transparent scrollbar-thumb-blue"
        >
          <template v-if="!customOptions">
            <div
              v-for="option of options"
              :key="option[itemId]"
              class="flex min-h-[3rem] cursor-pointer items-center px-6 py-[0.594rem]"
              :class="[
                activeOption && option[itemId] === activeOption[itemId]
                  ? 'bg-slate text-white'
                  : 'hover:bg-gray-lighten3',
              ]"
              @click.stop.prevent="selectOption(option)"
            >
              {{ option[itemText] }}
            </div>
          </template>
          <slot />
        </div>
      </div>
    </transition>

    <span v-if="error" class="block py-1 text-red-lighten1">
      {{ error }}
    </span>
  </label>
</template>

<script setup lang="ts">
import { computed, ref, provide } from 'vue';
import { onClickOutside, useToggle } from '@vueuse/core';

interface Props {
  modelValue?: string | number | null;
  modelModifiers?: Record<string, boolean>;
  label?: string | null;
  options?: any[];
  customOptions?: boolean;
  disabled?: boolean;
  placeholder?: string;
  error?: string | null;
  itemId?: string;
  itemText?: string;
  icon?: string;
  inputFitWidth?: boolean;
}

interface Emits {
  (e: 'update:modelValue', data: any): void;
}
const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  modelModifiers: () => ({}),
  label: null,
  options: () => [],
  customOptions: false,
  disabled: false,
  placeholder: undefined,
  error: null,
  itemId: 'id',
  itemText: 'text',
  icon: '',
  inputFitWidth: false,
});

const emit = defineEmits<Emits>();

const target = ref(null);
const isMenuOpen = ref(false);

const toggleMenu = useToggle(isMenuOpen);

const activeOption = computed(() =>
  props.options.find((option) => option[props.itemId] === props.modelValue),
);

const valueLocal = computed({
  get() {
    return activeOption.value ? activeOption.value[props.itemText] : '';
  },
  set(newValue) {
    emit('update:modelValue', newValue);
  },
});

const selectOption = (option: any) => {
  valueLocal.value = option[props.itemId];
  toggleMenu(false);
};

onClickOutside(target, () => {
  toggleMenu(false);
});

provide('selectData', {
  valueLocal,
  selectOption,
  itemId: props.itemId,
  itemText: props.itemText,
});
</script>

<style lang="sass" scoped>
.scrollbar
  &::-webkit-scrollbar
    width: 8px

  &::-webkit-scrollbar-thumb
    border-radius: 8px
    height: 75px

.input-select
  -webkit-tap-highlight-color: transparent
</style>
